import { ConfigProvider, theme as antdTheme } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

const fontStack = "'Switzer', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/** Light — ICS brandsheet: Forest / Lime / Tea / Thyme */
const lightTheme = {
  algorithm: antdTheme.defaultAlgorithm,
  token: {
    colorPrimary: '#142726',
    colorSuccess: '#49e580',
    colorLink: '#1a4d44',
    colorLinkHover: '#142726',
    colorInfo: '#1a4d44',
    borderRadius: 12,
    lineWidth: 1,
    lineType: 'solid',
    colorBorder: '#d4dfce',
    colorBorderSecondary: '#e3ead8',
    colorTextPlaceholder: '#8a9892',
    fontFamily: fontStack,
    colorText: '#142726',
    colorTextSecondary: '#657771',
    colorTextDescription: '#657771',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBgLayout: '#edf2e5',
    boxShadow: '0 1px 2px rgba(20, 39, 38, 0.04)',
    boxShadowSecondary: '0 8px 30px rgba(20, 39, 38, 0.06)',
    controlHeight: 42,
    controlOutline: 'rgba(73, 229, 128, 0.25)',
  },
  components: {
    Button: {
      primaryShadow: '0 1px 2px rgba(20, 39, 38, 0.06)',
      fontWeight: 600,
      controlHeight: 42,
    },
    Input: {
      activeBorderColor: '#142726',
      hoverBorderColor: '#9cb0a4',
      borderRadius: 12,
      paddingBlock: 10,
      paddingInline: 14,
      activeShadow: '0 0 0 3px rgba(73, 229, 128, 0.12)',
    },
    Select: {
      borderRadius: 12,
      optionSelectedBg: 'rgba(73, 229, 128, 0.16)',
      optionSelectedColor: '#142726',
      optionActiveBg: 'rgba(73, 229, 128, 0.1)',
    },
    Switch: {
      colorPrimary: '#142726',
    },
    Card: {
      borderRadiusLG: 16,
      paddingLG: 24,
    },
    Modal: {
      borderRadiusLG: 16,
    },
  },
};

/** Dark — Forest + Lime (ICS), no legacy purple */
const darkTheme = {
  algorithm: antdTheme.darkAlgorithm,
  token: {
    colorPrimary: '#49e580',
    colorSuccess: '#49e580',
    colorLink: '#7ef0a8',
    colorLinkHover: '#49e580',
    colorInfo: '#7ef0a8',
    colorBgBase: '#152825',
    colorBgContainer: '#1a312e',
    colorBgElevated: '#1e3834',
    colorBgLayout: '#152825',
    colorTextBase: '#edf2e5',
    colorText: 'rgba(237, 242, 229, 0.94)',
    colorTextSecondary: '#9cb0a4',
    colorTextDescription: 'rgba(237, 242, 229, 0.55)',
    colorTextPlaceholder: 'rgba(237, 242, 229, 0.35)',
    colorBorder: 'rgba(255, 255, 255, 0.09)',
    colorBorderSecondary: 'rgba(255, 255, 255, 0.06)',
    controlItemBgActive: '#1e3834',
    controlItemBgActiveHover: '#24403b',
    borderRadius: 12,
    lineWidth: 1,
    fontFamily: fontStack,
    controlHeight: 42,
    controlOutline: 'rgba(73, 229, 128, 0.25)',
  },
  components: {
    Button: {
      fontWeight: 600,
      controlHeight: 42,
    },
    Input: {
      borderRadius: 12,
      paddingBlock: 10,
      paddingInline: 14,
      activeShadow: '0 0 0 3px rgba(73, 229, 128, 0.2)',
    },
    Select: {
      borderRadius: 12,
      optionSelectedBg: 'rgba(73, 229, 128, 0.22)',
      optionSelectedColor: 'rgba(237, 242, 229, 0.98)',
      optionActiveBg: 'rgba(255, 255, 255, 0.08)',
    },
    Switch: {
      colorPrimary: '#49e580',
    },
    Card: {
      borderRadiusLG: 16,
    },
    Modal: {
      borderRadiusLG: 16,
    },
  },
};

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') === 'dark');

  useEffect(() => {
    const stored = localStorage.getItem('theme');
    const dark = stored === 'dark';
    setIsDark(dark);
    document.documentElement.classList.toggle('dark', dark);
  }, []);

  const config = useMemo(() => (isDark ? darkTheme : lightTheme), [isDark]);

  return (
    <ConfigProvider theme={config}>
      {children}
    </ConfigProvider>
  );
}
